import { ApiError, safeParseJson, toApiError } from "@/lib/api/client"
import {
  type AuthUser,
  type Company,
  type LoginPayload,
  type SignupPayload,
} from "@/lib/api/types"

export type { AuthUser, LoginPayload, SignupPayload }

export interface ClientAuthResult {
  user: AuthUser
  hasCompany: boolean
  companies: Company[]
  token?: string // JWT token from Rails (Devise)
}

async function postAuth(
  path: string,
  body: unknown,
): Promise<ClientAuthResult> {
  const response = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })

  const text = await response.text()
  const data = safeParseJson(text) as Record<string, unknown> | null

  if (!response.ok) {
    throw toApiError(data, response.status)
  }

  const result = data as unknown as ClientAuthResult

  // Extract and save JWT token for SSE authentication
  // Rails stores token in HttpOnly cookie, but also returns it in response headers or body
  if (typeof window !== "undefined") {
    try {
      // Try to get token from: response header → response body → extract from Cookie header
      let token = response.headers.get("X-Auth-Token") || (result.token as string)

      // If still no token, try to extract from Set-Cookie header (standar.auth.token)
      if (!token) {
        const setCookie = response.headers.get("Set-Cookie") || ""
        const match = setCookie.match(/standar\.auth\.token=([^;]+)/)
        if (match?.[1]) token = match[1]
      }

      if (token) {
        // Save to localStorage for SSE (EventSource can't access HttpOnly cookies)
        localStorage.setItem("auth_token", token)
        sessionStorage.setItem("auth_token", token)
      }
    } catch {
      // Silent fail if storage not available
    }
  }

  return result
}

export function signup(payload: SignupPayload): Promise<ClientAuthResult> {
  return postAuth("/api/auth/signup", payload)
}

export function login(payload: LoginPayload): Promise<ClientAuthResult> {
  return postAuth("/api/auth/login", payload)
}

export async function logout(): Promise<void> {
  const response = await fetch("/api/auth/logout", { method: "POST" })
  if (!response.ok && response.status !== 204) {
    throw new ApiError(response.status, [`Logout failed with status ${response.status}`])
  }

  // Clear JWT token from localStorage on logout
  if (typeof window !== "undefined") {
    try {
      localStorage.removeItem("auth_token")
    } catch {
      // Silent fail if localStorage not available
    }
  }
}
